import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, HeroIconName, HTMLParser, LineClamp, SvgIcon } from '@gik/ui';
import { getAnnouncementUIContextFromSlug } from '.';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import noop from '@gik/core/utils/noop';
import DateTimeDisplay from '../DateTimeDisplay/DateTimeDisplay';
import moment from 'moment';
import { isInPastAllTimezones } from '@gik/core/utils/DateTimeUtils';

export interface ICalendarAnnouncementProps extends React.HTMLAttributes<HTMLDivElement> {
  type: string;
  icon: HeroIconName;
  title: string;
  description: string;
  customMoreButtonClick?: (ev: React.MouseEvent<HTMLDivElement>) => void;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  displayTime?: boolean;
}

export default function CalendarAnnouncement({
  className,
  type,
  icon,
  title,
  description,
  customMoreButtonClick = noop,
  allDay,
  startsAt,
  endsAt,
  displayTime = true,
  ...otherProps
}: ICalendarAnnouncementProps): React.ReactElement {
  const bem = bemBlock('calendar-announcement');
  const { t } = useTranslation();

  const isPastEvent = isInPastAllTimezones(moment.utc(startsAt));

  const buttonContext = getAnnouncementUIContextFromSlug(type);
  const customMoreButton = (clamped: boolean) => (
    <div className={bem('btn-more')}>
      <Button variant={buttonContext} size="xs" pill>
        {clamped && t(translationKeys.moreButton)}
        {!clamped && t(translationKeys.lessButton)}
      </Button>
    </div>
  );

  return (
    <div className={bem(null, [{ [type]: type }, { past: isPastEvent }], className)} {...otherProps}>
      <header>
        <SvgIcon className={bem('icon')} name={icon} />

        {displayTime && (
          <DateTimeDisplay
            className="gik-hidden-md-down"
            timeSeparator=" to "
            hideDate
            hideTime={allDay}
            startsAt={startsAt}
            endsAt={endsAt}
            allDay={false}
          />
        )}
      </header>
      <main>
        <span className={bem('title')}>
          <HTMLParser rawHtml={title} />
        </span>
        {displayTime && (
          <DateTimeDisplay
            className="gik-hidden-md"
            timeSeparator=" to "
            hideDate
            hideTime={allDay}
            startsAt={startsAt}
            endsAt={endsAt}
            allDay={false}
          />
        )}
        <div className={bem('description')}>
          <LineClamp
            customButton={customMoreButton}
            lines={2}
            fadeOut={false}
            customButtonClick={customMoreButtonClick}
          >
            <HTMLParser rawHtml={description} />
          </LineClamp>
        </div>
      </main>
    </div>
  );
}
